import { Component, HostListener, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { UserService } from '@shared/services/user/user.service';
import { HttpClient } from "@angular/common/http";
import { combineLatest, Observable, Subject, switchMap, throwError } from "rxjs";
import { finalize, map, tap } from 'rxjs/operators';
import { PlannerProjectService } from "@modules/planner/services/planner-project/planner-project.service";
import { Project } from "@shared/models/project";
import { MapConfiguratorService } from "@shared/services/mapConfigurator/map-configurator.service";
import {
  CreateBasePayload,
  CreateRoutePayload,
  ROUTE_PLANNER_DRAW_OPTIONS, ROUTE_PLANNER_POLYLINE_COLOR, ROUTE_PLANNER_POLYLINE_HIGHLIGHT_COLOR,
  ROUTE_PLANNER_POLYLINE_SHAPE_OPTIONS,
  RoutePlanningActions,
  RoutePlanningBase,
  RoutePlanningRoute, UpdateBasePayload, UpdateRoutePayload, RouteNote
} from "@shared/models/route-planning";
import { ToastService } from "@shared/services/toast/toast.service";
import { TranslateService } from "@ngx-translate/core";
import { RoutePlanningService } from "@modules/planner/services/route-planning/route-planning.service";
import { UserDetails } from "@shared/models/user";
import { FormBuilder, FormGroup } from "@angular/forms";
import { WorkOrderService } from "@modules/planner/services/workorder/work-order.service";
import { WorkOrder } from "@shared/models/work-order";
import { MapUtils } from "@shared/services/mapUtils/map-utils.service";
import { Modal } from 'bootstrap';
import { CustomerIds } from "@shared/models/customer-settings";

declare const L: any;

type RouteNoteModalType = 'CREATE' | 'EDIT' | 'DELETE';

@Component({
    selector: 'app-route-planning.ts',
    templateUrl: './route-planning.component.html',
    styleUrls: ['./route-planning.component.scss'],
    providers: [HttpClient],
    standalone: false
})
export class RoutePlanningComponent implements OnInit, AfterViewInit {
  @ViewChild('routeNotesModal') routeNotesModalElement!: ElementRef;
  private routeNotesModalInstance!: Modal;
  public routeNoteMode: RouteNoteModalType = 'CREATE';
  public routeNoteText = '';
  public selectedRouteNote: RouteNote | null = null;

  private componentDestroyed$: Subject<void> = new Subject()
  private projectId!: number;
  private userId!: number;
  private projectName!: string;

  private map!: L.Map;
  private customerId: number | undefined = 0;
  private drawPolyLineTool: any;
  private createNewRoute: RoutePlanningRoute | null = null;
  private baseMarkers: Map<number, L.Marker> = new Map();
  private routePolyLines: Map<number, L.Layer> = new Map();
  private routePolyLineHitboxes: Map<number, L.Layer> = new Map();
  private blockMarker!: L.Marker;
  private powerLineWorkOrders: WorkOrder[] = [];
  private powerLineWorkOrderMarkers = new Map<number, L.Marker>();
  private routeNoteMarkers = new Map<number, L.Marker>();
  private lastRouteClickCoordinates: L.LatLng | null = null;
  private routeLengthTooltip: L.Tooltip | null = null;

  public activeAction: RoutePlanningActions = RoutePlanningActions.NONE;
  public actions = RoutePlanningActions;
  public pageLoading = true;
  public selectedRouteColor: string = ROUTE_PLANNER_POLYLINE_COLOR;
  public selectedRouteColorError: string | null = null;
  public defaultRouteColor = ROUTE_PLANNER_POLYLINE_COLOR;
  public selectedRoutePolyLineHighlight!: L.Polyline;
  public routeTooltip = L.tooltip({ permanent: false, direction: 'bottom', offset: L.point(0, 10) });
  public loading = false;
  public form!: FormGroup;
  public selectedItemForm!: FormGroup;
  public showSidebar = false;
  public selectedItem: { type: 'BASE' | 'ROUTE', item: RoutePlanningBase | RoutePlanningRoute } | null = null;
  public selectedItemColor: string = ROUTE_PLANNER_POLYLINE_COLOR;
  public selectedRouteLength: string = '';
  public selectedItemColorError: string | null = null;

  constructor(
    private userService: UserService,
    private plannerProjectService: PlannerProjectService,
    private mapConfiguratorService: MapConfiguratorService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private routePlanningService: RoutePlanningService,
    private fb: FormBuilder,
    private workOrderService: WorkOrderService,
  ) {
    this.form = this.fb.group({
      baseIdentifier: [''],
      routeIdentifier: [''],
    });

    this.selectedItemForm = this.fb.group({
      identifier: [''],
      notes: [''],
    });
  }

  ngOnInit() {
    this.init$()
    .pipe(finalize(() => (this.pageLoading = false)))
    .subscribe({
      next: (project) => {
        if (!project || !this.customerId) {
          this.toastService.sendToast(false, this.translateService.instant('planner.routePlanning.errorInit'));
          return;
        }

        this.map = this.mapConfiguratorService.initMap(this.customerId, ROUTE_PLANNER_DRAW_OPTIONS);
        this.onDrawCreatedListener();
        this.onMapClickedListener();
        this.onRightClickListener();
        this.getBases(this.projectId);
        this.getRoutes(this.projectId);
        if (this.customerId === CustomerIds.POWERLINES) {
          this.getRouteNotes(this.projectId);
        }
        this.createBlockMarker();
        this.fetchWorkOrders();
      },
      error: () => {
        this.toastService.sendToast(false, this.translateService.instant('planner.routePlanning.errorInit'));
      }
    });
  }

  ngAfterViewInit() {
    this.routeNotesModalInstance = new Modal(this.routeNotesModalElement.nativeElement);
  }

  private init$(): Observable<Project | null> {
    return this.getCurrentProjectId$().pipe(
      switchMap((projectId) =>
        projectId !== undefined && projectId !== null
          ? this.plannerProjectService.getProjectById(projectId)
          : throwError(() => new Error('Project ID is undefined'))
      )
    );
  }

  private getCurrentProjectId$(): Observable<number | undefined> {
    return this.userService.getUserInfo().pipe(
      tap((user: UserDetails) => {
        this.projectId = user.current_project;
        this.userId = user.id;
        this.customerId = user.customer_id;
      }),
      map(user => user.current_project)
    );
  }

  private fetchWorkOrders(): void {
    combineLatest([
      this.workOrderService.getWorkOrders(null, 4),
    ]).subscribe(([workOrders4]) => {
      this.powerLineWorkOrders = [...workOrders4];
      this.drawPowerLineWorkOrders();
      this.recenterMapOnWorkOrders();
    });
  }

  private recenterMapOnWorkOrders(): void {
    const center = this.getCenterFromWorkOrders(this.powerLineWorkOrders);
    if (center && this.map) {
      this.map.panTo([center.latitude, center.longitude]);
    }
  }

  private getCenterFromWorkOrders(workOrders: WorkOrder[]): { latitude: number; longitude: number } | null {
    if (!Array.isArray(workOrders) || workOrders.length === 0) return null;

    const points = workOrders
      .map(wo => ({
        lat: Number((wo as any).latitude),
        lng: Number((wo as any).longitude),
      }))
      .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));

    if (points.length === 0) return null;

    // center of bounding box
    let minLat = points[0].lat, maxLat = points[0].lat;
    let minLng = points[0].lng, maxLng = points[0].lng;

    for (const p of points) {
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }

    return {
      latitude: (minLat + maxLat) / 2,
      longitude: (minLng + maxLng) / 2,
    };
  }

  private drawPowerLineWorkOrders(): void {
    if (this.powerLineWorkOrderMarkers) {
      this.powerLineWorkOrderMarkers.forEach(marker => marker.remove());
    }

    this.powerLineWorkOrderMarkers.clear();

    for (let workOrder of this.powerLineWorkOrders) {
      const markerOld = this.powerLineWorkOrderMarkers.get(workOrder.id);
      if (markerOld) markerOld.remove();

      const marker = MapUtils.getPowerlineMarker(workOrder, this.map);

      marker.bindTooltip(`
        <div style="text-align: center;">
            <div>${workOrder.identifier}</div>
        </div>`, {
        direction: 'top',
        offset: [0, -10]
      });
    }
  }

  private onRightClickListener(): void {
    // disable right click
    this.map.on('contextmenu', (event: L.LeafletMouseEvent) => {
      event.originalEvent.preventDefault();
    });

    // fires when drawing route points
    this.map.on('draw:drawvertex', (e: any) => {
      if (this.activeAction === RoutePlanningActions.DRAW_ROUTE) {
        const latlngs = e.layers.getLayers().map((layer: any) => layer.getLatLng());
        const latest = latlngs[latlngs.length - 1];
        this.blockMarker.setLatLng([latest.lat, latest.lng]);

        // route length
        const coordinates = latlngs.map((ll: any) => ({ lat: ll.lat, lng: ll.lng }));
        const lengthInMeters = MapUtils.calculateRouteLength(coordinates);
        const formattedLength = MapUtils.formatRouteLength(lengthInMeters);
        this.updateRouteLengthTooltip(latest, formattedLength);
      }
    });
  }

  private updateRouteLengthTooltip(latLng: L.LatLng, text: string): void {
    if (this.routeLengthTooltip) {
      this.routeLengthTooltip.setLatLng(latLng);
      this.routeLengthTooltip.setContent(text);
    } else {
      this.routeLengthTooltip = L.tooltip({
        permanent: true,
        direction: 'top',
        offset: L.point(0, -15),
        className: 'route-length-tooltip'
      }).setLatLng(latLng)
        .setContent(text)
        .addTo(this.map);
    }
  }

  private removeRouteLengthTooltip(): void {
    if (this.routeLengthTooltip) {
      this.map.removeLayer(this.routeLengthTooltip);
      this.routeLengthTooltip = null;
    }
  }

  private onDrawCreatedListener(): void {
    this.map.on('draw:created', (event: any) => {
      if (!event) return;

      // only create if using ENTER
      if (this.activeAction === RoutePlanningActions.DRAW_ROUTE && !this.createNewRoute) {
        this.cancelAction();
        return;
      }

      const layer = event.layer as L.Layer & { routeId?: number, route?: RoutePlanningRoute };
      this.mapConfiguratorService.getEditableLayers().addLayer(layer);

      if (layer.routeId) {
        this.routePolyLines.set(layer.routeId, layer);

        // used to increase the click area of the polyline
        const hitboxPolyline = L.polyline(
          layer['_latlngs'],
          {
            color: '#000000',
            weight: 12,
            opacity: 0,
            interactive: true,
          }
        ).bindPopup(this.getRoutePopUp(layer.routeId));

        // Click to select route
        hitboxPolyline.on('click', (e: any) => {
          this.highlightPolyLine(layer as any);
          // Store the click coordinates for route note creation
          if (e.latlng) {
            this.lastRouteClickCoordinates = e.latlng;
          }
          if (layer.route) this.selectItem('ROUTE', layer.route);
        });

        hitboxPolyline.routeId = layer.routeId;
        this.mapConfiguratorService.getEditableLayers().addLayer(hitboxPolyline);
        this.routePolyLineHitboxes.set(layer.routeId, hitboxPolyline);
      }

      this.createNewRoute = null;
      this.cancelAction();
    });
  }

  private getBases(projectId: number): void {
    this.routePlanningService.getBases(projectId)
    .subscribe(bases => {
      bases.forEach(base => {
        this.createBaseIcon(base);
      });
    });
  }

  private getRoutes(projectId: number): void {
    this.routePlanningService.getRoutes(projectId)
    .subscribe(routes => {
      routes.forEach(route => {
        this.createRoutePolyline(route);
      });
    });
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnterKeyPress(event: KeyboardEvent): void {
    if (this.activeAction === RoutePlanningActions.DRAW_ROUTE) {
      this.finishRouteDraw();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscKeyPress(event: KeyboardEvent): void {
    if (this.activeAction === RoutePlanningActions.DRAW_ROUTE
        || this.activeAction === RoutePlanningActions.CREATE_BASE
    ) {
      this.cancelAction();
    }
  }

  private resetHighlight(): void {
    this.selectedItem = null;
    this.showSidebar = false;
    this.selectedItemForm.reset();

    if (this.selectedRoutePolyLineHighlight) this.selectedRoutePolyLineHighlight.remove()
    const allLayers = this.mapConfiguratorService.getLayers();
    const polyLines = allLayers.filter(layer => layer instanceof L.Polyline);
    polyLines.forEach((polyLine: any) => polyLine.setStyle({
      color: polyLine.options?.originalColor || ROUTE_PLANNER_POLYLINE_COLOR
    }));
  }

  private highlightPolyLine(selectedPolyLine: L.Polyline): void {
    this.resetHighlight();

    this.selectedRoutePolyLineHighlight = L.polyline(
      selectedPolyLine.getLatLngs(),
      {
        ...selectedPolyLine.options,
        color: this.isWhiteColor(selectedPolyLine.options?.color) ? 'black' : 'white',
        weight: 7
      }
    ).addTo(this.map);

    selectedPolyLine.bringToFront();
  }

  private isWhiteColor(colorHex: string | undefined): boolean {
    if (!colorHex) return false;
    const color = colorHex.toLowerCase();
    return color === 'white' || color === '#fff' || color === '#ffffff';
  }

  /**
   * Get the active polygon points from PolyLine tool and manually fire draw:created
   * @private
   */
  private finishRouteDraw(): void {
    if (this.drawPolyLineTool) {
      if (this.selectedRouteColorError) {
        this.toastService.sendToast(false, this.translateService.instant('planner.routePlanning.invalidColor'));
        return;
      }

      const points = this.drawPolyLineTool._poly.getLatLngs();
      if (points?.length <= 1) {
        this.toastService.sendToast(
          false,
          this.translateService.instant('planner.routePlanning.errorNotEnoughPoints'),
          null,
        );

        return;
      }

      const data: CreateRoutePayload = {
        project_id: this.projectId,
        coordinates: points,
        color: this.selectedRouteColor,
        identifier: this.form.get('routeIdentifier')?.value,
      };

      this.routePlanningService.createRoute(data)
      .subscribe({
        next: (route) => {
          const options = ROUTE_PLANNER_POLYLINE_SHAPE_OPTIONS;
          options.color = this.selectedRouteColor;
          options.originalColor = this.selectedRouteColor;

          const polylineLayer = L.polyline(points, options)
          .bindPopup(this.getRoutePopUp(route.id))
          .on('click', (e: any) => {
            this.highlightPolyLine(polylineLayer);
            // Store the click coordinates for route note creation
            if (e.latlng) {
              this.lastRouteClickCoordinates = e.latlng;
            }
          });

          polylineLayer.routeId = route.id;

          this.createNewRoute = route;
          this.createRoutePolyline(route, true);
          this.drawPolyLineTool.disable();

          this.toastService.sendToast(
            true,
            this.translateService.instant('planner.routePlanning.successRouteDrawn'),
            null,
            this.translateService.instant('planner.routePlanning.routeDrawn')
          );
        },
        error: () => {
          this.drawPolyLineTool.disable();
          this.cancelAction();
        }
      });
    }
  }

  private onMapClickedListener(): void {
    this.map.on('click', (event: L.LeafletMouseEvent) => {
      if (this.activeAction === RoutePlanningActions.CREATE_BASE) {
        if (this.loading) return;
        this.createBase(event.latlng);
      } else {
        this.resetHighlight();
      }
    });
  }

  private createBase(point: L.LatLng): void {
    this.loading = true;

    const data: CreateBasePayload = {
      project_id: this.projectId,
      lat: point.lat,
      lng: point.lng,
      identifier: this.form.get('baseIdentifier')?.value,
    };

    this.routePlanningService.createBase(data)
    .subscribe({
      next: (base) => {
        this.cancelAction();
        this.createBaseIcon(base);
        this.toastService.sendToast(
          true,
          this.translateService.instant('planner.routePlanning.successBaseCreated'),
          null,
          this.translateService.instant('planner.routePlanning.baseCreated')
        );
      },
      complete: () => {
        this.loading = false;
      },
      error: (err) => {
        this.cancelAction();
      },
    });
  }

  private setAction(action: RoutePlanningActions): void {
    this.activeAction = action;
  }

  enableCreateBase(): void {
    this.resetHighlight();
    this.setAction(RoutePlanningActions.CREATE_BASE);
  }

  cancelAction(): void {
    this.setAction(RoutePlanningActions.NONE);
    this.blockMarker.remove();
    this.removeRouteLengthTooltip();
    if (this.drawPolyLineTool) this.drawPolyLineTool.disable();
    this.form.reset();
  }

  enableDrawRoute(): void {
    this.resetHighlight();
    this.blockMarker.addTo(this.map);
    this.blockMarker.setLatLng({ lat: 0, lng: 0 });
    this.setAction(RoutePlanningActions.DRAW_ROUTE);
    this.drawPolyLineTool = this.mapConfiguratorService.createPolylineDrawTool();
    this.drawPolyLineTool.enable();
  }

  /**
   * Create a blocking marker that prevents clicks to the map
   * Used to prevent polyline tool from creating a line when clicking the last point
   * @private
   */
  private createBlockMarker(): void {
    const point = new L.LatLng(0, 0);

    const transparentIcon = L.icon({
      iconUrl: '/assets/icons/powerlines/route_point.png',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    this.blockMarker = L.marker(point, {
      icon: transparentIcon,
      zIndexOffset: 9999
    }).addTo(this.map);

    this.blockMarker.on('click', (e) => {
      L.DomEvent.stopPropagation(e);
    });
  }

  private createBaseIcon(base: RoutePlanningBase): void {
    const point = new L.LatLng(base.coordinates.lat, base.coordinates.lng);
    const customIcon = L.icon({
      iconUrl: '/assets/icons/powerlines/base.png',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -20]
    });

    const marker = L.marker(point, { icon: customIcon }).addTo(this.map);
    this.baseMarkers.set(base.id, marker);
    const title = base.identifier
      ? base.identifier
      : this.translateService.instant('planner.routePlanning.base') + ' #' + base.id;

    const tooltip = marker.bindTooltip(`
      <div class="title">${title}</div>
      ${base.notes ? '<div class="notes">' + base.notes + '</div>' : ''}
    `, {
      permanent: true,
      direction: 'bottom',
      offset: [0, 10]
    }).getTooltip();

    // needed to adjust the initial tooltip position because of white-space: break-spaces
    if (tooltip) tooltip.update();

    const markerLayer = marker.bindPopup(`
      <div class="title">${this.translateService.instant('planner.routePlanning.base')} #${base.id}</div>
      ${base.notes ? '<div class="notes">' + base.notes + '</div>' : ''}
    `).on('click', () => {
      this.resetHighlight();
      this.selectItem('BASE', base);
    });
  }

  private deleteBaseMarker(baseId: number): void {
    const marker = this.baseMarkers.get(baseId);
    if (marker) {
      this.map.removeLayer(marker);
      this.baseMarkers.delete(baseId);
    }
  }

  private deleteRoutePolyLine(routeId: number): void {
    const polyLine = this.routePolyLines.get(routeId);
    if (polyLine) {
      this.map.removeLayer(polyLine);
      this.routePolyLines.delete(routeId);
    }

    const polyLineHitbox = this.routePolyLineHitboxes.get(routeId);
    if (polyLineHitbox) {
      this.map.removeLayer(polyLineHitbox);
      this.routePolyLineHitboxes.delete(routeId);
    }
  }

  private getRoutePopUp(routeId: number): string {
    return `
      <div class="title">${this.translateService.instant('planner.routePlanning.route')} #${routeId}</div>
    `;
  }

  private registerRouteDeleteEvent(polyLineLayer: L.Polyline & { routeId: number }): void {
    polyLineLayer.on('popupopen', () => {
      const button = document.getElementById('deleteRoute');
      if (button) button.addEventListener('click', () => this.deleteRoute(polyLineLayer.routeId));
    });
  }

  private createRoutePolyline(route: RoutePlanningRoute, createNew = false): void {
    if (createNew) this.createNewRoute = route;
    if (route?.coordinates?.length < 2) return;
    const options = ROUTE_PLANNER_POLYLINE_SHAPE_OPTIONS;
    options.color = route.color || ROUTE_PLANNER_POLYLINE_COLOR;
    options.originalColor = route.color || ROUTE_PLANNER_POLYLINE_COLOR;

    const polylineLayer = L.polyline(route?.coordinates, options)
    .bindPopup(this.getRoutePopUp(route.id))
    .on('click', (e: any) => {
      this.highlightPolyLine(polylineLayer);
      // Store the click coordinates for route note creation
      if (e.latlng) {
        this.lastRouteClickCoordinates = e.latlng;
      }
      this.selectItem('ROUTE', route);
    });

    const title = route.identifier
      ? route.identifier
      : this.translateService.instant('planner.routePlanning.route') + ' #' + route.id;

    MapUtils.setPolylineTooltip(this.map, this.routeTooltip, polylineLayer, `
        <div class="title">${title}</div>
        ${route.notes ? '<div class="notes">' + route.notes + '</div>' : ''}
    `);

    polylineLayer.routeId = route.id;
    polylineLayer.route = route;

    this.map.fire('draw:created', {
      layer: polylineLayer,
      layerType: 'polyline',
    });
  }

  deleteRoute(routeId: number | undefined): void {
    if (!routeId) return;

    this.routePlanningService.removeRoute({ project_id: this.projectId, route_id: routeId })
      .subscribe({
        next: () => {
          this.resetHighlight();
          this.deleteRoutePolyLine(routeId);

          this.getRouteNotes(this.projectId);

          this.toastService.sendToast(
            true,
            this.translateService.instant('planner.routePlanning.successRouteDeleted'),
            null,
            this.translateService.instant('planner.routePlanning.routeDeleted')
          );
        },
        error: (err) => {
          this.toastService.sendToast(
            false, this.translateService.instant('planner.routePlanning.errorDeleteRoute'), null
          );
        }
      });
  }

  deleteBase(baseId: number | undefined): void {
    if (!baseId) return;

    this.routePlanningService.removeBase({ project_id: this.projectId, base_id: baseId })
    .subscribe({
      next: () => {
        this.resetHighlight();
        this.deleteBaseMarker(baseId);
        this.toastService.sendToast(
          true,
          this.translateService.instant('planner.routePlanning.successBaseDeleted'),
          null,
          this.translateService.instant('planner.routePlanning.baseDeleted')
        );
      },
      error: (err) => {
        this.toastService.sendToast(
          false, this.translateService.instant('planner.routePlanning.errorDeleteBase'), null
        );
      }
    });
  }

  onColorTextChange(value: any) {
    this.selectedRouteColorError = null;
    this.selectedItemColorError = null;

    const isValidHex = /^#([0-9A-F]{3}){1,2}$/i.test(value) && value.length === 7;
    if (!isValidHex) {
      this.selectedRouteColorError = this.translateService.instant('planner.routePlanning.invalidColor');
      this.selectedItemColorError = this.translateService.instant('planner.routePlanning.invalidColor');
      return;
    }

    this.selectedRouteColor = value;
    this.selectedItemColor = value;
  }

  resetRouteColor(): void {
    this.selectedRouteColor = ROUTE_PLANNER_POLYLINE_COLOR;
    this.selectedItemColor = ROUTE_PLANNER_POLYLINE_COLOR;
    this.selectedRouteColorError = null;
    this.selectedItemColorError = null;
  }

  closeSidebar(): void {
    this.resetHighlight();
    this.showSidebar = false;
  }

  selectItem(type: 'BASE' | 'ROUTE', item: RoutePlanningBase | RoutePlanningRoute): void {
    this.cancelAction();
    this.showSidebar = true;
    this.selectedItem = { type, item };
    this.selectedItemForm.setValue({ 'identifier': item.identifier, 'notes': item.notes });

    if (type === 'ROUTE') {
      const route = item as RoutePlanningRoute;
      this.selectedItemColor = route.color;

      // Calculate and set route length
      const lengthInMeters = MapUtils.calculateRouteLength(route.coordinates);
      this.selectedRouteLength = MapUtils.formatRouteLength(lengthInMeters);
    } else {
      this.selectedRouteLength = '';
    }
  }

  updateSelectedRoute() {
    if (!this.selectedItem?.item?.id) return;

    const data: UpdateRoutePayload = {
      route_id: this.selectedItem.item.id,
      project_id: this.projectId,
      identifier: this.selectedItemForm.get('identifier')?.value,
      notes: this.selectedItemForm.get('notes')?.value,
      color: this.selectedItemColor,
    }

    this.routePlanningService.updateRoute(data)
    .subscribe({
      next: (route) => {
        this.resetHighlight();
        this.closeSidebar();

        this.routePolyLines.forEach(polyLine => polyLine.remove());
        this.routePolyLines.clear();
        this.getRoutes(this.projectId);

        this.toastService.sendToast(
          true,
          this.translateService.instant('planner.routePlanning.successRouteUpdated'),
          null,
          this.translateService.instant('planner.routePlanning.routeUpdated')
        );
      },
      error: () => {
        this.resetHighlight();
        this.closeSidebar();
      }
    });
  }

  updateSelectedBase() {
    if (!this.selectedItem?.item?.id) return;

    const data: UpdateBasePayload = {
      base_id: this.selectedItem.item.id,
      project_id: this.projectId,
      identifier: this.selectedItemForm.get('identifier')?.value,
      notes: this.selectedItemForm.get('notes')?.value,
    }

    this.routePlanningService.updateBase(data)
      .subscribe({
        next: (base) => {
          this.resetHighlight();
          this.closeSidebar();

          this.baseMarkers.forEach((marker) => marker.remove());
          this.baseMarkers.clear();
          this.getBases(this.projectId);

          this.toastService.sendToast(
            true,
            this.translateService.instant('planner.routePlanning.successBaseUpdated'),
            null,
            this.translateService.instant('planner.routePlanning.baseUpdated')
          );
        },
        error: () => {
          this.resetHighlight();
          this.closeSidebar();
        }
      });
  }

  private getRouteNotes(projectId: number): void {
    this.routeNoteMarkers.forEach(marker => marker.removeFrom(this.map));
    this.routeNoteMarkers.clear();

    this.routePlanningService.getRouteNotes(projectId)
      .subscribe(routeNotes => {
        routeNotes.forEach(routeNote => {
          const marker = MapUtils.createRouteNoteIcon(routeNote);
          this.routeNoteMarkers.set(routeNote.id, marker);

          marker.bindTooltip(routeNote.notes, {
            direction: 'top',
            offset: [0, -10],
            className: 'route-note-tooltip'
          });

          marker.addTo(this.map);

          // Click to open edit modal and sidebar
          marker.on('click', () => {
            // Open the route sidebar
            const polyline = this.routePolyLines.get(routeNote.route_id) as any;
            if (polyline?.route) {
              this.highlightPolyLine(polyline);
              this.selectItem('ROUTE', polyline.route);
            }
            this.openRouteNotesModal('EDIT', routeNote);
          });
        });
      });
  }

  openRouteNotesModal(mode: RouteNoteModalType, routeNote?: RouteNote): void {
    this.selectedRouteNote = null;
    this.routeNoteText = '';

    if (routeNote) {
      this.selectedRouteNote = routeNote;
      this.routeNoteText = routeNote.notes;
    }

    this.routeNoteMode = mode;
    this.routeNotesModalInstance.show();
  }

  openCreateRouteNoteFromSidebar(): void {
    if (!this.selectedItem || this.selectedItem.type !== 'ROUTE') return;

    const route = this.selectedItem.item as RoutePlanningRoute;

    // Use the stored click coordinates if available, otherwise use center point
    let coordinates: L.LatLng;

    if (this.lastRouteClickCoordinates) {
      coordinates = this.lastRouteClickCoordinates;
    } else {
      // Fallback to center point if no click coordinates stored
      const routeCoordinates = route.coordinates;
      if (!routeCoordinates || routeCoordinates.length === 0) return;
      const centerIndex = Math.floor(routeCoordinates.length / 2);
      const centerCoord = routeCoordinates[centerIndex];
      coordinates = L.latLng(centerCoord.lat, centerCoord.lng);
    }

    // Create a temporary route note with the click coordinates
    const tempRouteNote: RouteNote = {
      id: 0,
      route_id: route.id,
      notes: '',
      coordinates: coordinates
    };

    this.openRouteNotesModal('CREATE', tempRouteNote);
  }

  createRouteNote() {
    if (!this.selectedRouteNote) return;

    this.selectedRouteNote.notes = this.routeNoteText;
    this.routePlanningService.createRouteNote({
      project_id: this.projectId,
      route_id: this.selectedRouteNote.route_id,
      coordinates: this.selectedRouteNote.coordinates,
      notes: this.selectedRouteNote.notes,
    }).subscribe(() => {
      this.routeNotesModalInstance.hide();
      this.getRouteNotes(this.projectId);
      this.toastService.sendToast(true, this.translateService.instant('planner.routePlanning.routeNoteCreated'));
    });
  }

  editRouteNote() {
    if (!this.selectedRouteNote) return;

    this.routePlanningService.updateRouteNote({
      project_id: this.projectId,
      route_note_id: this.selectedRouteNote.id,
      notes: this.routeNoteText,
    }).subscribe(() => {
      this.routeNotesModalInstance.hide();
      this.getRouteNotes(this.projectId);
      this.toastService.sendToast(true, this.translateService.instant('planner.routePlanning.routeNoteEdited'));
    });
  }

  deleteRouteNote() {
    this.routeNoteMode = 'DELETE';
  }

  dismissDeleteRouteNote() {
    this.routeNoteMode = 'EDIT';
  }

  confirmDeleteRouteNote() {
    if (!this.selectedRouteNote) return;

    this.routePlanningService.removeRouteNote({
      project_id: this.projectId,
      route_note_id: this.selectedRouteNote.id,
    }).subscribe(() => {
      this.routeNotesModalInstance.hide();
      this.getRouteNotes(this.projectId);
      this.toastService.sendToast(true, this.translateService.instant('planner.routePlanning.routeNoteDeleted'));
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
